/* eslint-disable @typescript-eslint/no-unsafe-assignment -- Datadog configuration. */
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { DatadogProvider } from '@datadog/openfeature-browser';
import { OpenFeature } from '@openfeature/react-sdk';

/**
 * Get Datadog configuration from environment variables
 */
export const getDatadogConfig = () => {
  const applicationId = import.meta.env.VITE_DATADOG_APPLICATION_ID;
  const clientToken = import.meta.env.VITE_DATADOG_CLIENT_TOKEN;
  const site = import.meta.env.VITE_DATADOG_SITE || 'datadoghq.com';
  // Use VITE_DATADOG_ENV if set, otherwise fall back to MODE (dev/prod)
  // The environment name must match an environment configured in your Datadog account
  const env =
    import.meta.env.VITE_DATADOG_ENV || import.meta.env.MODE || 'development';
  const version = import.meta.env.VITE_APP_VERSION || '1.0.0';
  const service = import.meta.env.VITE_DATADOG_SERVICE || 'wealth-access-demo';

  return {
    applicationId,
    clientToken,
    site,
    env,
    version,
    service,
  };
};

/**
 * Initialize Datadog RUM with React plugin and router tracking enabled
 * This should be called once at application startup
 */
export const initializeDatadogRum = () => {
  // Check if Datadog is already initialized to avoid double initialization
  if (datadogRum.getInitConfiguration()) {
    return;
  }

  const config = getDatadogConfig();

  // Only initialize if we have the required credentials
  if (!config.applicationId || !config.clientToken) {
    if (import.meta.env.DEV) {
      console.warn(
        'Datadog RUM not initialized: Missing VITE_DATADOG_APPLICATION_ID or VITE_DATADOG_CLIENT_TOKEN',
      );
    }
    return;
  }

  datadogRum.init({
    applicationId: config.applicationId,
    clientToken: config.clientToken,
    site: config.site,
    env: config.env,
    version: config.version,
    service: config.service,
    // Enable React plugin with router tracking
    plugins: [reactPlugin({ router: true })],
    // Sample rate (0-100)
    sessionSampleRate: 100,
    // Session replay sample rate (0-100)
    sessionReplaySampleRate: 20,
    // Track long tasks
    trackLongTasks: true,
    // Track resources
    trackResources: true,
    // Default privacy level
    defaultPrivacyLevel: 'mask',
    // Track feature flags for events
    // trackFeatureFlagsForEvents: ['action','resource', 'vital'],
    // Enable experimental features
    enableExperimentalFeatures: ['feature_flags'],
    // Compress intake requests
    compressIntakeRequests: true,
  });
};

let featureFlagProvider: DatadogProvider | null = null;

/**
 * Get the Datadog Feature Flags provider
 * Returns the provider if initialized, null otherwise
 */
export const getFeatureFlagProvider = (): DatadogProvider | null => {
  return featureFlagProvider;
};

/**
 * Initialize Datadog Feature Flags provider
 * This should be called once at application startup, after RUM is initialized
 * Only initializes if valid credentials are available to avoid fetch errors
 * Returns the provider that was set, or null if credentials are missing
 */
export const initializeDatadogFeatureFlags = (): DatadogProvider | null => {
  const config = getDatadogConfig();

  // Only create provider if we have valid credentials
  // Creating with empty credentials causes "Failed to fetch flag configuration" errors
  if (!config.applicationId || !config.clientToken) {
    if (import.meta.env.DEV) {
      console.warn(
        'Datadog Feature Flags not initialized: Missing VITE_DATADOG_APPLICATION_ID or VITE_DATADOG_CLIENT_TOKEN. Feature flags will use default values.',
      );
    }
    return null;
  }

  // Verify RUM is initialized before creating feature flag provider
  if (!datadogRum.getInitConfiguration()) {
    if (import.meta.env.DEV) {
      console.warn(
        'Datadog RUM not initialized. Feature Flags provider requires RUM to be initialized first.',
      );
    }
    return null;
  }

  try {
    // Verify RUM configuration matches
    const rumConfig = datadogRum.getInitConfiguration();
    if (rumConfig) {
      // Ensure feature flag provider uses the same configuration as RUM
      if (
        rumConfig.applicationId !== config.applicationId ||
        rumConfig.site !== config.site ||
        rumConfig.env !== config.env
      ) {
        if (import.meta.env.DEV) {
          console.warn(
            'Feature Flag provider configuration differs from RUM configuration. This may cause issues.',
          );
          console.warn('RUM config:', {
            applicationId: rumConfig.applicationId,
            site: rumConfig.site,
            env: rumConfig.env,
          });
          console.warn('Feature Flag config:', {
            applicationId: config.applicationId,
            site: config.site,
            env: config.env,
          });
        }
      }
    }

    // Create the Datadog provider with valid credentials
    // The provider will automatically connect to RUM if it's initialized
    // Note: The provider will attempt to fetch flag configurations asynchronously
    featureFlagProvider = new DatadogProvider({
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      site: config.site,
      env: config.env,
    });

    // Set the provider with initial evaluation context (for global usage)
    // The targetingKey is used for percentage-based targeting and randomization
    // IMPORTANT: The provider must be set BEFORE OpenFeatureProvider renders
    OpenFeature.setProvider(featureFlagProvider, {
      targetingKey: 'anonymous',
    });

    return featureFlagProvider;
  } catch (error) {
    // Handle synchronous initialization errors
    if (import.meta.env.DEV) {
      console.error('Failed to create Datadog Feature Flags provider:', error);
      console.warn('Feature flags will return default values');
    }
    return null;
  }
};

/**
 * Update the evaluation context for feature flags
 * Call this when user information becomes available (e.g., after login)
 */
export const updateFeatureFlagContext = (context: {
  targetingKey: string;
  user_id?: string;
  email?: string;
  user_role?: string;
  tier?: string;
  [key: string]: string | number | boolean | undefined;
}) => {
  // Filter out undefined values to match EvaluationContext type
  void (async () => {
    const cleanContext = Object.fromEntries(
      Object.entries(context).filter(([_, value]) => value !== undefined),
    ) as Record<string, string | number | boolean>;

    await OpenFeature.setContext(cleanContext);
  })().catch((error: unknown) => {
    if (import.meta.env.DEV) {
      console.error('Failed to update feature flag context:', error);
    }
  });
};
